// src/lib/layer_access.ts — DREPTURI PE STRAT ȘI PE ATRIBUT pentru rolurile INTERNE (viewer/editor).
//
// PUR (fără DB, importabil și din *.client.ts): modelul `versions.config.access` al unui strat, validarea
// lui FAIL-CLOSED la scriere, citirea lenientă și calculul drepturilor EFECTIVE ale unui rol —
// `{visible, writable, allow}` — în aceeași formă (`allow` = allow-list de chei, `null` = toate) pe
// care o folosește deja filtrul PUBLIC F10 (public.ts). Așa, fiecare punct de aplicare (geojson, tile,
// detaliu, istoric, căutare, identify, export, context) tratează rolul intern restricționat exact ca
// pe un vizitator al portalului: un singur tipar, nu două sisteme.
//
// Modelul (plan/contracts/drepturi_rol_strat_atribut.md):
//   access?: {
//     viewer?: "read" | "none"                                   // implicit "read"
//     editor?: "write" | "read" | "none"                         // implicit "write"
//     hidden_attributes?: { viewer?: string[]; editor?: string[] } // implicit []
//   }
// Configurație ABSENTĂ (sau toată pe implicit) = comportamentul de dinainte: viewer-ul citește tot,
// editorul scrie tot. `owner`/`admin` ai primăriei și platforma `super`/`support` nu sunt restricționați.
//
// ⚠️ `allow` e o ALLOW-LIST derivată din `attribute_schema` (cheile declarate ale stratului) minus cele
// ascunse, plus cheile de SISTEM (colectare/poze — pe care le judecă mai departe regulile existente,
// `can_view_system`/`must_redact`): o cheie care există în date dar NU e declarată în schemă nu se
// arată rolului restricționat (fail-closed). Fără nicio cheie ascunsă, `allow` e `null` = toate.

import type { Validation } from "./validation"
import { validation_error } from "./validation"
import { SYSTEM_FEATURE_KEYS } from "./collect"

export const VIEWER_LEVELS = ["read", "none"] as const
export const EDITOR_LEVELS = ["write", "read", "none"] as const
export type ViewerLevel = (typeof VIEWER_LEVELS)[number]
export type EditorLevel = (typeof EDITOR_LEVELS)[number]

/** Rolurile de org RESTRICȚIONABILE pe strat (owner/admin nu sunt). */
export const ACCESS_ROLES = ["viewer", "editor"] as const
export type AccessRestrictedRole = (typeof ACCESS_ROLES)[number]
/** Rolul EFECTIV al unui apelant pe harta unei primării: restricționabil sau nu. */
export type AccessRole = AccessRestrictedRole | "unrestricted"

/** Forma COMPLETĂ (normalizată) a configurației — toate cheile prezente. */
export interface LayerAccessConfig {
	viewer: ViewerLevel
	editor: EditorLevel
	hidden_attributes: { viewer: string[]; editor: string[] }
}

/** Forma MINIMĂ stocată în `config.access`: doar abaterile de la implicit. */
export interface LayerAccessStored {
	viewer?: "none"
	editor?: "read" | "none"
	hidden_attributes?: { viewer?: string[]; editor?: string[] }
}

export const DEFAULT_LAYER_ACCESS: LayerAccessConfig = Object.freeze({
	viewer: "read",
	editor: "write",
	hidden_attributes: Object.freeze({
		viewer: Object.freeze([]) as unknown as string[],
		editor: Object.freeze([]) as unknown as string[],
	}),
})

/** Drepturile EFECTIVE ale unui apelant pe un strat. */
export interface LayerAccess {
	visible: boolean
	/** Scrierea nu e INTERZISĂ de configurația stratului (rangul de org îl verifică gărzile de rută). */
	writable: boolean
	/** Allow-list de chei de atribut (`null` = toate) — aceeași semantică ca F10 pe calea publică. */
	allow: string[] | null
}
export const FULL_ACCESS: LayerAccess = Object.freeze({
	visible: true,
	writable: true,
	allow: null,
})
export const NO_ACCESS: LayerAccess = Object.freeze({
	visible: false,
	writable: false,
	allow: Object.freeze([]) as unknown as string[],
})

/** Mesajele de refuz (D9): stratul VIZIBIL dar doar-citire → 403 explicit; invizibil → 404 indistinct. */
export const LAYER_READ_ONLY_MESSAGE =
	"Stratul este doar citire pentru rolul tău în această organizație."
export const hidden_attribute_write_message = (key: string): string =>
	`Atributul '${key}' nu este vizibil rolului tău în această organizație — nu poate fi modificat.`
export const ACCESS_ADMIN_ONLY_MESSAGE =
	"Drepturile pe strat le configurează doar administratorii primăriei (owner/admin)."

export const VIEWER_LEVEL_LABELS: Record<ViewerLevel, string> = {
	read: "văd stratul",
	none: "nu văd stratul",
}
export const EDITOR_LEVEL_LABELS: Record<EditorLevel, string> = {
	write: "pot scrie",
	read: "doar citesc",
	none: "nu văd stratul",
}

/** Plafoane: o listă de atribute ascunse peste 200 de chei sau o cheie de peste 200 de caractere nu e o configurație, e o greșeală. */
export const MAX_HIDDEN_ATTRIBUTES = 200
const MAX_KEY_LENGTH = 200

const ALLOWED_TOP_KEYS = new Set(["viewer", "editor", "hidden_attributes"])
const ALLOWED_HIDDEN_KEYS = new Set(["viewer", "editor"])

function is_obj(v: unknown): v is Record<string, unknown> {
	return typeof v === "object" && v !== null && !Array.isArray(v)
}

/** Lista de chei ascunse a unui rol: șiruri NEVIDE, trim-uite, fără duplicate; `undefined` = invalidă. */
function hidden_list(raw: unknown, who: string): Validation<string[]> {
	if (raw === undefined || raw === null) {
		return { ok: true, value: [] }
	}
	if (!Array.isArray(raw)) {
		return validation_error(`access.hidden_attributes.${who} trebuie să fie o listă de chei`)
	}
	const out: string[] = []
	for (const k of raw) {
		if (typeof k !== "string") {
			return validation_error(`access.hidden_attributes.${who}: cheile trebuie să fie text`)
		}
		const key = k.trim()
		if (key === "") {
			return validation_error(`access.hidden_attributes.${who}: cheie goală`)
		}
		if (key.length > MAX_KEY_LENGTH) {
			return validation_error(
				`access.hidden_attributes.${who}: cheia '${key.slice(0, 20)}…' e prea lungă`,
			)
		}
		if (!out.includes(key)) {
			out.push(key)
		}
	}
	if (out.length > MAX_HIDDEN_ATTRIBUTES) {
		return validation_error(
			`access.hidden_attributes.${who}: cel mult ${MAX_HIDDEN_ATTRIBUTES} chei ascunse`,
		)
	}
	return { ok: true, value: out }
}

/**
 * VALIDAREA la scriere (FAIL-CLOSED): orice cheie sau valoare necunoscută e refuzată cu mesaj RO.
 * `null`/`undefined` → `null` (= scoate configurația). Întoarce forma COMPLETĂ, normalizată.
 */
export function validate_layer_access(raw: unknown): Validation<LayerAccessConfig | null> {
	if (raw === null || raw === undefined) {
		return { ok: true, value: null }
	}
	if (!is_obj(raw)) {
		return validation_error("access trebuie să fie un obiect {viewer, editor, hidden_attributes}")
	}
	for (const k of Object.keys(raw)) {
		if (!ALLOWED_TOP_KEYS.has(k)) {
			return validation_error(`access: cheie necunoscută '${k}'`)
		}
	}
	let viewer: ViewerLevel = "read"
	if (raw.viewer !== undefined && raw.viewer !== null) {
		if (!(VIEWER_LEVELS as readonly unknown[]).includes(raw.viewer)) {
			return validation_error(
				`access.viewer: valoare necunoscută (permise: ${VIEWER_LEVELS.join(", ")})`,
			)
		}
		viewer = raw.viewer as ViewerLevel
	}
	let editor: EditorLevel = "write"
	if (raw.editor !== undefined && raw.editor !== null) {
		if (!(EDITOR_LEVELS as readonly unknown[]).includes(raw.editor)) {
			return validation_error(
				`access.editor: valoare necunoscută (permise: ${EDITOR_LEVELS.join(", ")})`,
			)
		}
		editor = raw.editor as EditorLevel
	}
	let hidden_viewer: string[] = []
	let hidden_editor: string[] = []
	if (raw.hidden_attributes !== undefined && raw.hidden_attributes !== null) {
		if (!is_obj(raw.hidden_attributes)) {
			return validation_error("access.hidden_attributes trebuie să fie un obiect {viewer, editor}")
		}
		for (const k of Object.keys(raw.hidden_attributes)) {
			if (!ALLOWED_HIDDEN_KEYS.has(k)) {
				return validation_error(`access.hidden_attributes: cheie necunoscută '${k}'`)
			}
		}
		const hv = hidden_list(raw.hidden_attributes.viewer, "viewer")
		if (!hv.ok) {
			return hv
		}
		const he = hidden_list(raw.hidden_attributes.editor, "editor")
		if (!he.ok) {
			return he
		}
		hidden_viewer = hv.value
		hidden_editor = he.value
	}
	return {
		ok: true,
		value: { viewer, editor, hidden_attributes: { viewer: hidden_viewer, editor: hidden_editor } },
	}
}

/** Configurația e toată pe implicit (= echivalentă cu absența ei)? */
export function access_is_default(cfg: LayerAccessConfig): boolean {
	return (
		cfg.viewer === "read" &&
		cfg.editor === "write" &&
		cfg.hidden_attributes.viewer.length === 0 &&
		cfg.hidden_attributes.editor.length === 0
	)
}

/** Forma MINIMĂ de stocat: doar abaterile de la implicit (`null` când nu există niciuna). */
export function minimal_layer_access(cfg: LayerAccessConfig): LayerAccessStored | null {
	if (access_is_default(cfg)) {
		return null
	}
	const out: LayerAccessStored = {}
	if (cfg.viewer !== "read") {
		out.viewer = cfg.viewer
	}
	if (cfg.editor !== "write") {
		out.editor = cfg.editor
	}
	const hidden: { viewer?: string[]; editor?: string[] } = {}
	if (cfg.hidden_attributes.viewer.length > 0) {
		hidden.viewer = [...cfg.hidden_attributes.viewer]
	}
	if (cfg.hidden_attributes.editor.length > 0) {
		hidden.editor = [...cfg.hidden_attributes.editor]
	}
	if (Object.keys(hidden).length > 0) {
		out.hidden_attributes = hidden
	}
	return out
}

/**
 * CITIREA lenientă a valorii din DB (jsonb auto-parsat de Bun sau string JSON): întoarce configurația
 * COMPLETĂ sau `null` când lipsește / e goală / e toată pe implicit — apelanții sar atunci peste
 * rezolvarea identității (fără configurație, nimic nu costă în plus). Valorile nerecunoscute degradează
 * spre implicit (doctrina casei: poarta fail-closed e SCRIEREA, `validate_layer_access`).
 */
export function parse_layer_access(raw: unknown): LayerAccessConfig | null {
	if (raw === null || raw === undefined) {
		return null
	}
	let parsed: unknown = raw
	if (typeof raw === "string") {
		try {
			parsed = JSON.parse(raw)
		} catch {
			return null
		}
	}
	if (!is_obj(parsed)) {
		return null
	}
	const viewer: ViewerLevel = parsed.viewer === "none" ? "none" : "read"
	const editor: EditorLevel =
		parsed.editor === "read" || parsed.editor === "none" ? parsed.editor : "write"
	const hidden = is_obj(parsed.hidden_attributes) ? parsed.hidden_attributes : {}
	const cfg: LayerAccessConfig = {
		viewer,
		editor,
		hidden_attributes: { viewer: lenient_list(hidden.viewer), editor: lenient_list(hidden.editor) },
	}
	return access_is_default(cfg) ? null : cfg
}

/** Lista de chei dintr-o valoare citită lenient: doar șirurile nevide, trim-uite, fără duplicate. */
function lenient_list(v: unknown): string[] {
	if (!Array.isArray(v)) {
		return []
	}
	const out: string[] = []
	for (const k of v) {
		if (typeof k === "string" && k.trim() !== "" && !out.includes(k.trim())) {
			out.push(k.trim())
		}
	}
	return out
}

/** Cheile declarate ale unui `attribute_schema` (oricare din cele două formate istorice). */
export function schema_keys_of(attribute_schema: unknown): string[] {
	return is_obj(attribute_schema) ? Object.keys(attribute_schema) : []
}

/**
 * Drepturile EFECTIVE ale unui rol pe strat. `unrestricted` → tot; `viewer`/`editor` → după configurație.
 * `allow` = cheile din schemă ∪ cheile de sistem, minus cele ascunse rolului; `null` fără chei ascunse.
 * Stratul INVIZIBIL → `NO_ACCESS` (nu e nici scriibil, nici citibil).
 */
export function layer_access_by_role(
	role: AccessRole,
	cfg: LayerAccessConfig | null,
	schema_keys: readonly string[],
): LayerAccess {
	if (role === "unrestricted" || cfg === null) {
		return FULL_ACCESS
	}
	const visible = role === "viewer" ? cfg.viewer !== "none" : cfg.editor !== "none"
	if (!visible) {
		return NO_ACCESS
	}
	// Rolul `viewer` nu scrie oricum (gărzile de rută cer editor+); configurația nu-i poate interzice
	// mai mult decât are — `writable` rămâne pe vizibilitate (regula de viewer pe hărțile fără primărie
	// lasă scrierea gărzilor, ca azi).
	const writable = role === "viewer" ? true : cfg.editor === "write"
	const hidden = new Set(cfg.hidden_attributes[role])
	if (hidden.size === 0) {
		return { visible: true, writable, allow: null }
	}
	const allow: string[] = []
	for (const k of [...schema_keys, ...SYSTEM_FEATURE_KEYS]) {
		if (!hidden.has(k) && !allow.includes(k)) {
			allow.push(k)
		}
	}
	return { visible: true, writable, allow }
}

/** Cheile păstrate pe lângă allow-list: identificatorul tehnic al obiectului (ca pe calea publică). */
const KEEP_EXTRA = new Set(["feature_id"])

/** Filtrarea unui dicționar de proprietăți prin allow-list (`null` = neatins). */
export function access_filter_props(
	props: Record<string, unknown>,
	allow: string[] | null,
): Record<string, unknown> {
	if (allow === null) {
		return props
	}
	return Object.fromEntries(
		Object.entries(props).filter(([k]) => allow.includes(k) || KEEP_EXTRA.has(k)),
	)
}

/** Prima cheie din `props` pe care rolul NU o vede (deci n-o poate scrie), sau "" dacă nu e niciuna. */
export function hidden_key_in(
	props: Record<string, unknown> | undefined,
	allow: string[] | null,
): string {
	if (!props || allow === null) {
		return ""
	}
	for (const k of Object.keys(props)) {
		if (!allow.includes(k) && !KEEP_EXTRA.has(k)) {
			return k
		}
	}
	return ""
}

/** Semnătura allow-list-ului pentru ETag-uri și chei de cache: `*` = toate, altfel cheile SORTATE. */
export function allow_signature(allow: string[] | null): string {
	return allow === null ? "*" : allow.toSorted().join(",")
}
